/**
 * PercentileBands Component
 *
 * Pure CSS horizontal bar chart for displaying percentile bands
 * Zero dependencies (uses only MUI components already in project)
 * High performance with CSS transitions for real-time updates
 *
 * @component
 * @example
 * <PercentileBands
 *   percentileBands={{
 *     p99: 563.44, p95: 562.08, p75: 559.05, p50: 554.04,
 *     p25: 541.57, p5: 535.02, p1: 532.68,
 *     mean: 550.71, stdDev: 9.75, count: 119
 *   }}
 *   currentValue={562.08}
 *   primarySymbol="XAUUSD"
 * />
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

const PercentileBands = ({ percentileBands, currentValue, primarySymbol = '' }) => {
    // Memoize bands configuration for performance
    const bands = useMemo(() => {
        // Validation inside useMemo
        if (!percentileBands || typeof percentileBands !== 'object') {
            return [];
        }
        const p1 = percentileBands.p1 ?? percentileBands.P1 ?? 0;
        const p5 = percentileBands.p5 ?? percentileBands.P5 ?? 0;
        const p25 = percentileBands.p25 ?? percentileBands.P25 ?? 0;
        const p50 = percentileBands.p50 ?? percentileBands.P50 ?? percentileBands.median ?? 0;
        const p75 = percentileBands.p75 ?? percentileBands.P75 ?? 0;
        const p95 = percentileBands.p95 ?? percentileBands.P95 ?? 0;
        const p99 = percentileBands.p99 ?? percentileBands.P99 ?? 0;

        return [
            { label: 'P99', value: p99, color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.08)' },
            { label: 'P95', value: p95, color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.08)' },
            { label: 'P75', value: p75, color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.08)' },
            { label: 'P50', value: p50, color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.08)' },
            { label: 'P25', value: p25, color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.08)' },
            { label: 'P5', value: p5, color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.08)' },
            { label: 'P1', value: p1, color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.08)' }
        ];
    }, [percentileBands]);

    // Calculate min/max for scaling
    const { minValue, maxValue } = useMemo(() => {
        const values = bands.map(b => b.value).filter(v => v > 0);
        if (values.length === 0) return { minValue: 0, maxValue: 100 };

        const min = Math.min(...values);
        const max = Math.max(...values);
        const range = max - min;

        // Add 5% padding on each side
        return {
            minValue: min - (range * 0.05),
            maxValue: max + (range * 0.05)
        };
    }, [bands]);

    // Format value with proper precision
    const formatValue = (value) => {
        if (value === 0) return '-';
        if (value > 1000) return value.toFixed(1);
        if (value > 100) return value.toFixed(2);
        return value.toFixed(3);
    };

    // Return null if no valid data (after all hooks)
    if (!percentileBands || bands.length === 0) {
        return null;
    }

    return (
        <Box sx={{
            padding: '12px',
            backgroundColor: 'rgba(10, 10, 10, 0.6)',
            borderRadius: '4px',
            border: '1px solid #262626'
        }}>
            {/* Header */}
            <Typography sx={{
                fontSize: '9px',
                fontWeight: 700,
                color: '#9ca3af',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: '10px'
            }}>
                Percentile Bands
            </Typography>

            {/* Bands */}
            {bands.map((band, idx) => {
                const width = maxValue > minValue
                    ? ((band.value - minValue) / (maxValue - minValue)) * 100
                    : 0;

                const isCurrent = currentValue && Math.abs(currentValue - band.value) < (maxValue - minValue) * 0.02;

                return (
                    <Box
                        key={idx}
                        sx={{
                            marginBottom: idx === bands.length - 1 ? 0 : '5px',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {/* Label */}
                        <Typography sx={{
                            width: '32px',
                            fontSize: '9px',
                            color: '#6b7280',
                            fontWeight: 700,
                            flexShrink: 0
                        }}>
                            {band.label}
                        </Typography>

                        {/* Bar Container */}
                        <Box sx={{
                            flex: 1,
                            height: '14px',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '2px',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '1px solid #262626',
                            marginLeft: '6px',
                            marginRight: '6px'
                        }}>
                            {/* Bar Fill */}
                            <Box sx={{
                                width: `${Math.max(0, Math.min(100, width))}%`,
                                height: '100%',
                                backgroundColor: band.color,
                                transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                                opacity: isCurrent ? 1 : 0.6,
                                boxShadow: isCurrent ? `0 0 10px ${band.color}` : 'none',
                                position: 'relative'
                            }}>
                                {/* Current indicator */}
                                {isCurrent && (
                                    <Box sx={{
                                        position: 'absolute',
                                        right: '3px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '8px',
                                        color: '#fff',
                                        fontWeight: 900
                                    }}>
                                        ◆
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        {/* Value */}
                        <Typography sx={{
                            width: '55px',
                            textAlign: 'right',
                            fontSize: '10px',
                            color: isCurrent ? '#ffffff' : '#9ca3af',
                            fontWeight: isCurrent ? 700 : 600,
                            fontFamily: 'Courier New, monospace',
                            flexShrink: 0,
                            transition: 'color 0.3s ease'
                        }}>
                            {formatValue(band.value)}
                        </Typography>
                    </Box>
                );
            })}

            {/* Statistics Footer */}
            <Box sx={{
                marginTop: '10px',
                paddingTop: '8px',
                borderTop: '1px solid #262626',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography sx={{ fontSize: '8px', color: '#6b7280', fontWeight: 600 }}>
                    Mean: <span style={{ color: '#9ca3af', fontFamily: 'Courier New' }}>
                        {formatValue(percentileBands.mean ?? 0)}
                    </span>
                </Typography>
                <Typography sx={{ fontSize: '8px', color: '#6b7280', fontWeight: 600 }}>
                    StdDev: <span style={{ color: '#9ca3af', fontFamily: 'Courier New' }}>
                        {formatValue(percentileBands.stdDev ?? 0)}
                    </span>
                </Typography>
                <Typography sx={{ fontSize: '8px', color: '#6b7280', fontWeight: 600 }}>
                    Bars: <span style={{ color: '#9ca3af' }}>
                        {percentileBands.count ?? 0}{percentileBands.minBars ? ` (≥${percentileBands.minBars})` : ''}
                    </span>
                </Typography>
            </Box>
        </Box>
    );
};

// Default export
export default React.memo(PercentileBands);
